import { useEffect, useState } from 'react'
import { Plus, Trash2, Star } from 'lucide-react'
import {
  MAXIMO_DE_CANDIDATOS_INTERNOS,
  candidatoEnBlanco,
  comoElegible,
  conElSeleccionado,
  opcionesDeEmpleados,
  puestoDe,
  type CandidatoInterno,
  type EmpleadoElegible,
} from '@/features/rh/lib/candidatosInternos'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { employeesService } from '@/features/rh/api/employees.service'

/**
 * Gente de casa que podría cubrir la vacante, hasta dos (RH-01).
 *
 * Las filas se manejan por índice y no por id porque todavía no lo tienen: el
 * id lo pone la base al guardar, y mientras se editan son solo posiciones.
 * Mismo criterio que la paquetería requerida, de la que copia la forma.
 */
export const CandidatosInternos = ({
  filas,
  onChange,
  deshabilitado = false,
}: {
  filas: CandidatoInterno[]
  onChange: (filas: CandidatoInterno[]) => void
  /**
   * En consulta se pintan los mismos controles, apagados, y no un texto
   * aparte: así la sección no cambia de forma entre ver y editar.
   */
  deshabilitado?: boolean
}) => {
  const [empleados, setEmpleados] = useState<EmpleadoElegible[]>([])
  const [cargando, setCargando] = useState(false)

  /**
   * La plantilla se pide una vez, y solo cuando se puede editar: en consulta
   * los controles van apagados y la lista no se usaría para nada.
   */
  useEffect(() => {
    if (deshabilitado) return
    let vigente = true
    setCargando(true)
    employeesService
      .getForSelect()
      .then((lista) => vigente && setEmpleados(lista.map(comoElegible)))
      .catch(() => vigente && setEmpleados([]))
      .finally(() => vigente && setCargando(false))
    return () => {
      vigente = false
    }
  }, [deshabilitado])

  /**
   * Al elegir a alguien se llena también su puesto, que es dato del
   * expediente y no algo que quien captura tenga que recordar. Ya no se edita:
   * lo que se manda es el puesto del empleado elegido, y la pantalla lo dice
   * debajo del select para que se sepa cuál va.
   */
  const elegir = (indice: number, nombre: string) => {
    const puesto = puestoDe(empleados, nombre)
    onChange(
      filas.map((fila, i) =>
        i === indice ? { ...fila, name: nombre, currentPosition: puesto } : fila,
      ),
    )
  }

  const agregar = () => onChange([...filas, candidatoEnBlanco()])

  const quitar = (indice: number) =>
    onChange(filas.filter((_, i) => i !== indice))

  const marcar = (indice: number) => onChange(conElSeleccionado(filas, indice))

  return (
    <div>
      {filas.length === 0 && (
        <p className="text-sm text-gray-400 italic mb-3">
          Sin candidatos internos considerados
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        {filas.map((fila, indice) => (
          <div key={indice} className="min-w-0">
            <div className="flex gap-3 items-start">
              <SelectBuscable
                variante="formulario"
                className="flex-1"
                value={fila.name}
                onChange={(nombre) => elegir(indice, nombre)}
                disabled={deshabilitado || cargando}
                aria-label={`Empleado ${indice + 1}`}
                placeholder={cargando ? 'Cargando empleados…' : 'Selecciona al empleado'}
                placeholderDeBusqueda="Buscar empleado…"
                opciones={opcionesDeEmpleados(empleados, filas, indice).map((nombre) => ({
                  value: nombre,
                  label: nombre,
                }))}
              />

              {/*
                La marca es un botón y no una casilla porque elegir a uno
                desmarca al otro: es una sola respuesta repartida entre las
                filas, no dos preguntas sueltas. Va junto al select para que se
                vea de quién es.
              */}
              <span className="relative group inline-flex">
                <button
                  type="button"
                  onClick={() => marcar(indice)}
                  disabled={deshabilitado}
                  aria-pressed={fila.isSelected === true}
                  aria-label={
                    fila.isSelected
                      ? 'Es el candidato seleccionado'
                      : 'Marcar como candidato seleccionado'
                  }
                  className={`p-2 rounded-md transition-colors disabled:cursor-default ${
                    fila.isSelected
                      ? 'text-amber-500'
                      : 'text-gray-300 hover:text-amber-400 disabled:hover:text-gray-300'
                  }`}
                >
                  <Star
                    className="w-5 h-5"
                    fill={fila.isSelected ? 'currentColor' : 'none'}
                  />
                </button>
                <span
                  role="tooltip"
                  className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity z-10"
                >
                  {fila.isSelected
                    ? 'Candidato seleccionado (favorito)'
                    : 'Marcar como candidato seleccionado (favorito)'}
                </span>
              </span>

              {!deshabilitado && (
                <button
                  type="button"
                  onClick={() => quitar(indice)}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  aria-label={`Quitar ${fila.name || 'candidato'}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            {fila.name && (
              <p className="mt-1 text-xs text-gray-500">
                {fila.currentPosition
                  ? `Este empleado cuenta con el puesto ${fila.currentPosition}`
                  : 'Este empleado no tiene un puesto registrado'}
              </p>
            )}
          </div>
        ))}
      </div>

      {!deshabilitado && !cargando && empleados.length === 0 && filas.length > 0 && (
        <p className="mt-3 text-xs text-amber-600">
          No se pudo cargar la plantilla. Vuelve a entrar a la pantalla para
          elegir de la lista.
        </p>
      )}

      {!deshabilitado && filas.length < MAXIMO_DE_CANDIDATOS_INTERNOS && (
        <button
          type="button"
          onClick={agregar}
          className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[#477FFA] hover:text-[#2f63cc] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Agregar candidato interno
        </button>
      )}

      {!deshabilitado && filas.length >= MAXIMO_DE_CANDIDATOS_INTERNOS && (
        <p className="mt-3 text-xs text-gray-500">
          Máximo {MAXIMO_DE_CANDIDATOS_INTERNOS} candidatos internos.
        </p>
      )}
    </div>
  )
}
